<template>
  <div class="flex flex-col bg-gray-50 h-full min-h-0 flex-1">
    <PageHeader
      v-if="!embedded"
      title="Agent"
      subtitle="Explore the warehouse and author artifacts with a live agent — beta"
    />
    <div class="flex-1 min-h-0 flex flex-col px-4 pb-4" :class="embedded ? 'pt-1' : 'pt-3'">
    <!-- state banner -->
    <div class="flex items-center gap-2 pb-2">
      <span class="w-2 h-2 rounded-full" :class="dotClass" />
      <span class="text-xs text-gray-500">{{ stateLabel }}</span>
      <span v-if="s.totalCost" class="text-xs text-gray-400 tabular-nums">
        session cost ${{ s.totalCost.toFixed(2) }}
      </span>
      <div class="ml-auto flex items-center gap-2">
        <button
          v-if="!sessionActive"
          type="button"
          class="text-xs px-2 py-0.5 border rounded text-gray-600 hover:bg-gray-50"
          @click="toggleHistory"
        >
          History
        </button>
        <button
          v-if="!sessionActive"
          type="button"
          class="text-xs px-2 py-0.5 border rounded text-gray-600 hover:bg-gray-50"
          @click="startLive"
        >
          {{ replay ? 'Start new session' : (s.connState === 'idle' ? (startLabel || 'Start session') : 'Reconnect') }}
        </button>
        <button
          v-else
          type="button"
          class="text-xs px-2 py-0.5 border rounded text-gray-400 hover:text-red-600 hover:border-red-300"
          @click="endSession"
        >
          End session
        </button>
      </div>
    </div>

    <div v-if="contextMismatch" class="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      This active session was started without this metric’s Wizard context. End it, then start the Wizard session.
    </div>

    <!-- session history -->
    <div v-if="showHistory" class="border rounded-lg bg-white mb-2 max-h-56 overflow-y-auto">
      <p v-if="!pastSessions.length" class="p-3 text-xs text-gray-400">No past sessions.</p>
      <button
        v-for="ps in pastSessions"
        :key="ps.session_id"
        type="button"
        class="w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 flex items-baseline gap-3"
        @click="openSession(ps.session_id)"
      >
        <span class="text-sm text-gray-800 truncate flex-1">{{ ps.title || '(no message)' }}</span>
        <span class="text-xs text-gray-400 whitespace-nowrap">{{ new Date(ps.created_at).toLocaleString() }}</span>
        <span class="text-xs text-gray-400 tabular-nums">${{ (ps.total_cost_usd || 0).toFixed(2) }}</span>
        <span class="text-[11px]" :class="ps.status === 'died' ? 'text-red-400' : 'text-gray-300'">{{ ps.status }}</span>
      </button>
    </div>

    <div ref="panes" class="flex-1 min-h-0 flex">
      <!-- ─────────── LEFT: conversation ─────────── -->
      <div class="flex-1 min-w-0 flex flex-col">
        <div ref="scroll" class="flex-1 min-h-0 overflow-y-auto border rounded-lg bg-white">
          <div v-if="!s.blocks.length" class="p-8 text-sm text-gray-400 max-w-xl">
            <p class="font-medium text-gray-500 mb-2">Explore the warehouse with a live agent.</p>
            <p>
              It queries Snowflake and Postgres as you talk, tests assumptions,
              and draws what it finds on the scratchpad to the right. When
              you're happy, tell it to save — metrics, flags, worklists and
              insights land in the studio directly.
            </p>
            <p class="mt-2 text-xs">
              The agent loads ~500KB of warehouse context when you start a session.
              It introduces itself when ready (usually a minute or two). The session
              survives navigating the app; it ends if you close or reload the page.
            </p>
          </div>

          <div v-for="(b, i) in s.blocks" :key="i" class="px-4 py-1.5">
            <div v-if="b.kind === 'user'" class="max-w-3xl rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
              <div class="flex items-center gap-2 text-[11px] font-medium text-primary-700 mb-1">
                <span class="w-1.5 h-1.5 rounded-full bg-primary-500" />
                You
              </div>
              <p class="text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">{{ b.text }}</p>
            </div>

            <div v-else-if="b.kind === 'text'" class="max-w-3xl rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
              <div class="flex items-center gap-2 text-[11px] font-medium text-sky-700 mb-1">
                <span class="w-1.5 h-1.5 rounded-full bg-sky-500" />
                Agent
              </div>
              <p class="text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">{{ b.text }}</p>
            </div>

            <div v-else-if="b.kind === 'question'" class="max-w-3xl rounded-md border border-violet-200 bg-violet-50 px-3 py-2">
              <div class="flex items-center gap-2 text-[11px] font-medium text-violet-700 mb-1">
                <span class="w-1.5 h-1.5 rounded-full bg-violet-500" />
                Decision needed
              </div>
              <p class="text-xs leading-relaxed text-gray-700 mb-2">{{ b.question }}</p>
              <div class="grid gap-1.5">
                <button
                  v-for="option in b.options"
                  :key="option.label"
                  type="button"
                  class="text-left rounded border px-2.5 py-2 text-xs transition-colors"
                  :class="b.answered === option.label
                    ? 'border-violet-500 bg-violet-100 text-violet-900'
                    : 'border-violet-200 bg-white text-gray-700 hover:border-violet-400'"
                  :disabled="!!b.answered"
                  @click="answerQuestion(b, option.label)"
                >
                  <span class="font-medium">{{ option.label }}</span>
                  <span class="block text-gray-500 mt-0.5">{{ option.description }}</span>
                </button>
              </div>
              <div v-if="!b.answered" class="flex gap-1.5 mt-2">
                <input
                  v-model="b.responseText"
                  class="flex-1 rounded border border-violet-200 bg-white px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                  placeholder="Or write your answer"
                  @keydown.enter.prevent="answerQuestion(b)"
                />
                <button
                  type="button"
                  class="rounded border border-violet-300 px-2.5 text-xs text-violet-700 hover:bg-violet-100 disabled:opacity-40"
                  :disabled="!b.responseText || !b.responseText.trim()"
                  @click="answerQuestion(b)"
                >Answer</button>
              </div>
              <p v-else class="mt-2 text-[11px] text-violet-700">Answer: {{ b.answered }}</p>
            </div>

            <button
              v-else-if="b.kind === 'thinking'"
              type="button"
              class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-left"
              :class="[
                b.collapsed ? 'w-full' : 'max-w-3xl',
                b.done ? 'cursor-pointer hover:border-gray-300' : 'cursor-default',
              ]"
              :disabled="!b.done"
              @click="b.collapsed = !b.collapsed"
            >
              <div class="flex items-center gap-2 text-[11px] font-medium text-gray-500" :class="b.collapsed ? '' : 'mb-1'">
                <span class="w-1.5 h-1.5 rounded-full" :class="b.done ? 'bg-gray-300' : 'bg-green-500 animate-pulse'" />
                Thinking
                <span v-if="b.done" class="ml-auto text-gray-400 font-normal">
                  {{ b.collapsed ? 'Click to expand' : 'Click to collapse' }}
                </span>
              </div>
              <p
                v-if="!b.collapsed"
                class="text-xs leading-relaxed text-gray-600 whitespace-pre-wrap"
              >{{ b.text }}</p>
            </button>

            <button
              v-else-if="b.kind === 'visual_ref'"
              type="button"
              class="flex items-center gap-2 text-xs text-sky-700 hover:underline"
              @click="scrollToVisual(b.visualId)"
            >
              <span>📊</span>
              <span>{{ b.title }} → scratchpad</span>
            </button>

            <div
              v-else-if="b.kind === 'created'"
              class="my-1 max-w-xl border rounded-lg px-3 py-2"
              :class="b.ok ? 'border-green-300 bg-green-50' : 'border-red-200 bg-red-50'"
            >
              <p v-if="b.ok" class="text-sm text-green-800">
                <span class="font-semibold">{{ b.artifactLabel }} {{ b.action }}</span> — #{{ b.id }} {{ b.name }}
              </p>
              <p v-else class="text-sm text-red-700">{{ b.artifactLabel }} create failed: {{ b.error }}</p>
            </div>

            <p v-else-if="b.kind === 'error'" class="text-xs text-red-600">{{ b.text }}</p>
            <p v-else-if="b.kind === 'meta'" class="text-[11px] text-gray-300 tabular-nums">{{ b.text }}</p>
          </div>

          <div v-if="thinking" class="px-4 py-2 text-xs text-gray-400">
            <span class="animate-pulse">working… {{ thinkingElapsed }}</span>
          </div>
        </div>

        <!-- composer -->
        <div class="flex items-end gap-2 pt-2">
          <textarea
            v-model="draft"
            rows="2"
            class="flex-1 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-300"
            :placeholder="composerPlaceholder"
            :disabled="hasPendingQuestion || (s.connState !== 'ready' && s.connState !== 'thinking')"
            @keydown.enter.exact.prevent="sendDraft"
          />
          <button
            v-if="thinking"
            type="button"
            class="px-3 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
            @click="interrupt"
          >
            Stop
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg bg-orange-600 text-white disabled:opacity-40"
            :disabled="!draft.trim() || hasPendingQuestion || (s.connState !== 'ready' && s.connState !== 'thinking')"
            @click="sendDraft"
          >
            Send
          </button>
        </div>
      </div>

      <!-- divider: drag to resize, double-click to reset -->
      <div
        class="ae-divider"
        title="Drag to resize · double-click to reset"
        @mousedown.prevent="startDivider"
        @dblclick="resetDivider"
      />

      <!-- ─────────── RIGHT: scratchpad + reference panels ─────────── -->
      <div
        class="min-w-0 flex flex-col"
        :style="{ width: padPct + '%', flexShrink: 0 }"
      >
        <!-- TOP: scratchpad (half height, paged) -->
        <div class="flex-1 min-h-0 flex flex-col border rounded-lg bg-gray-50/70">
          <div class="flex items-center gap-2 px-3 py-2 border-b bg-white rounded-t-lg">
            <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Scratchpad</span>
            <template v-if="s.visuals.length">
              <button
                type="button"
                class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                :disabled="padPage <= 0"
                @click="padPage--"
              >
                <font-awesome-icon :icon="['fas', 'chevron-left']" />
              </button>
              <span class="text-xs text-gray-500 tabular-nums">{{ padPage + 1 }} / {{ s.visuals.length }}</span>
              <button
                type="button"
                class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                :disabled="padPage >= s.visuals.length - 1"
                @click="padPage++"
              >
                <font-awesome-icon :icon="['fas', 'chevron-right']" />
              </button>
            </template>
            <button
              v-if="s.visuals.length"
              type="button"
              class="ml-auto text-xs text-gray-400 hover:text-gray-600"
              @click="s.visuals = []; padPage = 0"
            >
              Clear
            </button>
          </div>
          <div class="flex-1 min-h-0 overflow-hidden p-3 flex flex-col">
            <p v-if="!s.visuals.length" class="text-xs text-gray-400 p-4">
              Visuals the agent draws land here.
            </p>

            <template v-else>
              <div
                :key="currentVisual.id"
                class="bg-white border rounded-lg p-3 flex-1 min-h-0 overflow-y-auto"
              >
                <div class="flex items-baseline gap-2 pb-2">
                  <span class="text-sm font-semibold text-gray-700">{{ currentVisual.title }}</span>
                  <span class="text-[11px] text-gray-400">{{ currentVisual.viz_type }}</span>
                </div>

                <iframe
                  v-if="currentVisual.viz_type === 'drawing'"
                  :srcdoc="drawingDoc(currentVisual)"
                  sandbox=""
                  class="w-full border-0"
                  :style="{ aspectRatio: drawingAspect(currentVisual) }"
                  :title="currentVisual.title"
                />

                <div v-else-if="!currentVisual.viz" class="text-xs text-red-600">Could not render this visual.</div>

                <Chart_V6
                  v-else-if="currentVisual.viz.component === 'chart_v6'"
                  :spec="currentVisual.viz.props.spec"
                />

                <div v-else-if="currentVisual.viz.component === 'chart_v5'" class="h-[320px]">
                  <Chart_V5
                    :chart_data="currentVisual.viz.props.chart_data"
                    :chart_config="currentVisual.viz.props.chart_config"
                    override-height="h-full"
                  />
                </div>

                <div v-else-if="currentVisual.viz.component === 'table'" data-private class="h-[320px]">
                  <AgGridVue
                    :theme="listTheme"
                    style="width: 100%; height: 100%"
                    :column-defs="currentVisual.viz.props.columnDefs"
                    :default-col-def="defaultColDef"
                    :row-data="currentVisual.viz.props.rowData"
                  />
                </div>

                <Pie_Chart
                  v-else-if="currentVisual.viz.component === 'pie'"
                  class="h-[320px]"
                  override-height="h-full"
                  :chart_data="currentVisual.viz.props.chart_data"
                  :chart_config="currentVisual.viz.props.chart_config"
                />

                <Sankey_Diagram
                  v-else-if="currentVisual.viz.component === 'sankey'"
                  :chart-data="currentVisual.viz.props.chartData"
                  :config="currentVisual.viz.props.config"
                />

                <HexMap
                  v-else-if="currentVisual.viz.component === 'heatmap'"
                  :chart-data="currentVisual.viz.props.chartData"
                  :chart-config="currentVisual.viz.props.chartConfig"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- BOTTOM: schema tables + data sources side by side -->
        <div class="flex gap-2 pt-2" style="height: 50%">
          <!-- Available Tables -->
          <div class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Available Tables</span>
              <span v-if="erdSchemas.length" class="text-xs text-gray-400">{{ erdTableCount }}</span>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto px-3 py-2">
              <p v-if="!erdSchemas.length" class="text-xs text-gray-400">Loading catalog…</p>
              <div v-for="schema in erdSchemas" :key="schema.name" class="mb-2">
                <button
                  type="button"
                  class="w-full text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 flex items-center gap-1"
                  @click="schema._open = !schema._open"
                >
                  <font-awesome-icon
                    :icon="['fas', schema._open ? 'chevron-down' : 'chevron-right']"
                    class="text-[8px] text-gray-400"
                  />
                  {{ schema.name }}
                  <span class="font-normal text-gray-400 ml-1">{{ Object.keys(schema.tables).length }}</span>
                </button>
                <div v-if="schema._open" class="pl-3 mt-1 space-y-0.5">
                  <div
                    v-for="(table, tname) in schema.tables"
                    :key="tname"
                    class="text-[11px] text-gray-600 truncate"
                    :title="table.description || tname"
                  >
                    {{ tname }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Data Sources (agent-declared) -->
          <div class="flex-1 min-w-0 flex flex-col border rounded-lg bg-white">
            <div class="flex items-center gap-2 px-3 py-2 border-b">
              <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Data Sources</span>
              <span v-if="s.sources.length" class="text-xs text-gray-400">{{ s.sources.length }}</span>
            </div>
            <div class="flex-1 min-h-0 overflow-y-auto px-3 py-2">
              <p v-if="!s.sources.length" class="text-xs text-gray-400">
                The agent declares data sources as it plans the metric.
              </p>
              <div
                v-for="src in s.sources"
                :key="src.name"
                class="mb-2 border rounded px-2 py-1.5"
              >
                <div class="flex items-center gap-1.5">
                  <span
                    class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    :class="{
                      'bg-green-500': src.status === 'ready',
                      'bg-amber-400': src.status === 'building',
                      'bg-gray-300': !src.status || src.status === 'planned',
                    }"
                  />
                  <span class="text-[11px] font-medium text-gray-700 truncate">{{ src.name }}</span>
                  <span v-if="src.role" class="text-[10px] text-gray-400 ml-auto flex-shrink-0">{{ src.role }}</span>
                </div>
                <p v-if="src.description" class="text-[10px] text-gray-500 mt-0.5 leading-tight">{{ src.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue';
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import HexMap from '@/components/widgetcomponents/Viz_Elements/HexMap.vue';
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue';
import { buildVizProps } from '@/utils/metricInsightAdapter';
import { standardListTheme } from '@/utils/common';
import { drawingDoc } from '@/utils/agentSandbox';

const CLOSE_LABELS = {
  4401: 'Not signed in.',
  4403: 'Requires tenant admin access.',
  4404: 'Unknown tenant.',
  4503: 'Agent sessions only run in local development for now.',
  4504: 'The agent process died — check /tmp/agent_session_*.log on the backend host.',
};

// SESSION LIVES AT MODULE SCOPE so switching studio tabs (which unmounts this
// component) does not kill the websocket — and with it the agent's container.
// Diagnosed 2026-08-13: every "dead container" was exitCode=0, i.e. the
// browser socket closing on unmount/reload. Tab switches now survive; a page
// close or reload still ends the session (the socket dies with the page).
const SESSION = reactive({
  connState: 'idle',   // idle | connecting | launching | ready | thinking | closed
  closeReason: '',
  blocks: [],
  visuals: [],
  sources: [],
  totalCost: 0,
  thinkingSince: null,
  retryInSeconds: null,
  extraSystemPrompt: '',
});
let sessionWs = null;
let activeView = null;   // the mounted component, for autoscroll only

function handleEvent(ev) {
  if (ev.type === 'ping') return;
  if (ev.type === 'status') {
    if (ev.state === 'launching' || ev.state === 'starting') SESSION.connState = 'launching';
    else if (ev.state === 'rate_limited') {
      SESSION.connState = 'rate_limited';
      SESSION.retryInSeconds = ev.retry_in_seconds;
    }
    else if (ev.state === 'ready') {
      SESSION.connState = 'ready';
      SESSION.thinkingSince = null;
    } else if (ev.state === 'thinking') {
      SESSION.connState = 'thinking';
      SESSION.thinkingSince = Date.now();
    }
    return;
  }
  if (ev.type === 'user') {
    // Only present in STORED streams — live user turns are pushed locally by
    // sendDraft. Lets replay run through this same pipeline.
    pushBlock({ kind: 'user', text: ev.text });
  } else if (ev.type === 'text') {
    pushBlock({ kind: 'text', text: ev.text });
  } else if (ev.type === 'text_delta') {
    let block = [...SESSION.blocks].reverse().find((b) => b.kind === 'text' && b.id === ev.id);
    if (!block) {
      block = { kind: 'text', id: ev.id, text: '' };
      pushBlock(block);
    }
    block.text += ev.text;
    if (activeView) activeView.scrollChat();
  } else if (ev.type === 'text_end') {
    // The streamed text block is already rendered; end only marks its protocol boundary.
  } else if (ev.type === 'ask_user') {
    pushBlock({ kind: 'question', callId: ev.call_id, question: ev.question,
      options: ev.options || [], responseText: '', answered: '' });
  } else if (ev.type === 'user_answer') {
    const block = [...SESSION.blocks].reverse().find((b) => b.kind === 'question' && b.callId === ev.call_id);
    if (block) block.answered = ev.label || ev.text || 'Written response';
  } else if (ev.type === 'thinking_start') {
    pushBlock({ kind: 'thinking', id: ev.id, text: '', done: false, collapsed: false });
  } else if (ev.type === 'thinking_delta') {
    let block = [...SESSION.blocks].reverse().find((b) => b.kind === 'thinking' && b.id === ev.id);
    if (!block) {
      block = { kind: 'thinking', id: ev.id, text: '', done: false, collapsed: false };
      pushBlock(block);
    }
    block.text += ev.text;
    if (activeView) activeView.scrollChat();
  } else if (ev.type === 'thinking_end') {
    const block = [...SESSION.blocks].reverse().find((b) => b.kind === 'thinking' && b.id === ev.id);
    if (block) {
      block.done = true;
      block.collapsed = true;
    }
  } else if (ev.type === 'drawing') {
    upsertVisual({ id: ev.id, title: ev.title || 'Untitled', viz_type: 'drawing', svg: ev.svg });
  } else if (ev.type === 'visual') {
    // The insight pipeline renders the scratchpad: same adapter, same
    // components — a visual here IS a saveable metric-insight config.
    let viz = null;
    try {
      viz = buildVizProps(ev.viz_type, ev.viz_config, ev.rows);
    } catch {
      viz = null;
    }
    upsertVisual({ id: ev.id, title: ev.title || 'Untitled', viz_type: ev.viz_type, viz });
  } else if (ev.type === 'scratchpad_clear') {
    if (ev.ids && ev.ids.length) {
      SESSION.visuals = SESSION.visuals.filter((v) => !ev.ids.includes(v.id));
    } else {
      SESSION.visuals = [];
    }
  } else if (ev.type === 'sources_update') {
    SESSION.sources = ev.sources || [];
  } else if (ev.type === 'create_result') {
    const labels = {
      metric: 'Metric', flag: 'Patient flag', worklist: 'Worklist', insight: 'Insight',
      data_attribute: 'DataAttribute', update_data_attribute: 'DataAttribute',
    };
    pushBlock({
      kind: 'created',
      ok: ev.ok,
      artifactLabel: labels[ev.artifact_kind] || 'Artifact',
      action: ev.action || 'created',
      id: ev.id,
      name: ev.name,
      error: ev.error,
    });
  } else if (ev.type === 'turn_done') {
    if (ev.cost_usd) SESSION.totalCost += ev.cost_usd;
    pushBlock({
      kind: 'meta',
      text: `— ${ev.turns ?? '?'} turns${ev.cost_usd ? ` · $${ev.cost_usd.toFixed(2)}` : ''}`,
    });
  } else if (ev.type === 'error' || ev.type === 'fatal') {
    pushBlock({ kind: 'error', text: ev.message });
  }
}

function pushBlock(block) {
  SESSION.blocks.push(block);
  if (activeView) activeView.scrollChat();
}

// The agent curates the pad: same id replaces the card in place (iteration),
// a new id appends. Only fresh cards get a conversation chip — updates don't
// re-announce.
function upsertVisual(v) {
  const i = SESSION.visuals.findIndex((x) => x.id === v.id);
  if (i >= 0) {
    SESSION.visuals.splice(i, 1, v);
  } else {
    SESSION.visuals.push(v);
    pushBlock({ kind: 'visual_ref', title: v.title, visualId: v.id });
  }
  if (activeView) activeView.padPage = SESSION.visuals.length - 1;
}

export default {
  name: 'AgentExplore',
  components: { PageHeader, AgGridVue, Chart_V5, Chart_V6, Sankey_Diagram, HexMap, Pie_Chart },
  props: {
    embedded: { type: Boolean, default: false },
    autoStart: { type: Boolean, default: false },
    extraSystemPrompt: { type: String, default: '' },
    metricId: { type: [Number, String], default: null },
    startLabel: { type: String, default: '' },
    // Which instance of the agent to run. 'metric' (default) is Metric Studio;
    // 'ingestion' points the same session at a file on the /data tab.
    sessionMode: { type: String, default: 'metric' },
    s3Key: { type: String, default: '' },
    schemaId: { type: [Number, String], default: null },
  },
  data() {
    return {
      s: SESSION,
      draft: '',
      now: Date.now(),
      ticker: null,
      defaultColDef: { sortable: true, resizable: true, filter: true },
      listTheme: standardListTheme,
      showHistory: false,
      pastSessions: [],
      replay: null,   // {session_id, title, created_at} while viewing a past session
      erdSchemas: [],
      padPct: Number(localStorage.getItem('agent_pad_pct')) || 60,
      padPage: 0,
      dragging: false,
    };
  },
  computed: {
    tenantSlug() {
      return this.$store.state.tenant.current_tenant_slug;
    },
    sessionActive() {
      return ['connecting', 'launching', 'ready', 'thinking', 'rate_limited'].includes(this.s.connState);
    },
    thinking() {
      return this.s.connState === 'thinking';
    },
    hasPendingQuestion() {
      return this.s.blocks.some((b) => b.kind === 'question' && !b.answered);
    },
    contextMismatch() {
      return this.sessionActive && !!this.extraSystemPrompt
        && this.s.extraSystemPrompt !== this.extraSystemPrompt;
    },
    thinkingElapsed() {
      if (!this.s.thinkingSince) return '';
      const secs = Math.max(0, Math.floor((this.now - this.s.thinkingSince) / 1000));
      return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
    },
    stateLabel() {
      if (this.replay) {
        return `viewing past session — ${this.replay.title || this.replay.session_id}`;
      }
      const st = this.s.connState;
      if (st === 'idle') return 'no session';
      if (st === 'connecting') return 'connecting…';
      if (st === 'launching') return 'starting the agent — it introduces itself when the context is loaded (~1-2 min)…';
      if (st === 'ready') return 'agent ready';
      if (st === 'thinking') return `agent working — ${this.thinkingElapsed}`;
      if (st === 'rate_limited') return `Vertex rate-limited — retrying in ${this.s.retryInSeconds || '?'}s…`;
      return this.s.closeReason || 'disconnected';
    },
    dotClass() {
      const st = this.s.connState;
      if (st === 'ready') return 'bg-green-500';
      if (st === 'thinking') return 'bg-orange-400';
      if (st === 'rate_limited') return 'bg-amber-400';
      if (st === 'closed' || st === 'idle') return 'bg-gray-300';
      return 'bg-yellow-400';
    },
    currentVisual() {
      if (!this.s.visuals.length) return null;
      const idx = Math.min(this.padPage, this.s.visuals.length - 1);
      return this.s.visuals[idx];
    },
    erdTableCount() {
      return this.erdSchemas.reduce((n, s) => n + Object.keys(s.tables).length, 0);
    },
    composerPlaceholder() {
      if (this.s.connState === 'closed') return this.stateLabel;
      return 'Ask about the data, or state an assumption to test… (Enter to send)';
    },
  },
  mounted() {
    activeView = this;
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
    if (this.autoStart && (!sessionWs || sessionWs.readyState >= WebSocket.CLOSING)) {
      if (this.s.connState !== 'closed') this.connect();
    }
    this.scrollChat();
    this.fetchErd();
  },
  beforeUnmount() {
    // Deliberately does NOT close the websocket — the session survives tab
    // switches. End it with the End session button or by leaving the page.
    if (activeView === this) activeView = null;
    clearInterval(this.ticker);
  },
  methods: {
    wsUrl() {
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/';
      let url;
      try {
        url = new URL(apiBase, window.location.origin);
      } catch {
        url = new URL(apiBase);
      }
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = `/ws/agent-session/${this.tenantSlug}/`;
      if (SESSION.extraSystemPrompt) {
        url.searchParams.set('extra_system_prompt', SESSION.extraSystemPrompt);
      }
      if (this.metricId) {
        url.searchParams.set('metric_id', this.metricId);
      }
      if (this.sessionMode && this.sessionMode !== 'metric') {
        url.searchParams.set('session_mode', this.sessionMode);
      }
      // The consumer re-validates this against the tenant's own S3 prefix —
      // never trust the browser with a key into a shared bucket.
      if (this.s3Key) {
        url.searchParams.set('s3_key', this.s3Key);
      }
      if (this.schemaId) {
        url.searchParams.set('schema_id', this.schemaId);
      }
      return url.toString();
    },
    connect() {
      SESSION.connState = 'connecting';
      SESSION.closeReason = '';
      SESSION.extraSystemPrompt = this.extraSystemPrompt;
      SESSION.blocks = [];
      SESSION.visuals = [];
      SESSION.sources = [];
      SESSION.totalCost = 0;
      const ws = new WebSocket(this.wsUrl());
      sessionWs = ws;
      ws.onmessage = (e) => handleEvent(JSON.parse(e.data));
      ws.onclose = (e) => {
        SESSION.connState = 'closed';
        SESSION.thinkingSince = null;
        SESSION.closeReason = CLOSE_LABELS[e.code] || `Connection closed (${e.code}).`;
      };
    },
    endSession() {
      if (sessionWs) sessionWs.close();
      SESSION.connState = 'closed';
      SESSION.closeReason = 'Session ended.';
    },
    startLive() {
      this.replay = null;
      this.showHistory = false;
      this.connect();
    },
    startDivider() {
      this.dragging = true;
      const move = (e) => {
        const box = this.$refs.panes && this.$refs.panes.getBoundingClientRect();
        if (!box || !box.width) return;
        const pct = ((box.right - e.clientX) / box.width) * 100;
        this.padPct = Math.min(70, Math.max(25, Math.round(pct * 10) / 10));
      };
      const up = () => {
        this.dragging = false;
        localStorage.setItem('agent_pad_pct', String(this.padPct));
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    resetDivider() {
      this.padPct = 60;
      localStorage.setItem('agent_pad_pct', '60');
    },
    async toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        const { data } = await axios.get('/api/impact_analytics/agent-sessions/');
        this.pastSessions = Array.isArray(data) ? data : (data.results || []);
      }
    },
    async openSession(sessionId) {
      const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/`);
      this.showHistory = false;
      this.replay = { session_id: data.session_id, title: data.title, created_at: data.created_at };
      SESSION.connState = 'closed';
      SESSION.closeReason = '';
      SESSION.blocks = [];
      SESSION.visuals = [];
      SESSION.totalCost = 0;   // turn_done events rebuild it during replay
      // Stored events are the live protocol verbatim — replay through the
      // same pipeline that renders a live session.
      (data.events || []).forEach((ev) => handleEvent(ev));
    },
    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    scrollPad() {
      this.$nextTick(() => {
        const el = this.$refs.padScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    drawingDoc,
    drawingAspect(v) {
      const m = /viewBox\s*=\s*["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)/.exec(v.svg || '');
      if (m && Number(m[2]) > 0) return `${m[1]} / ${m[2]}`;
      return '2 / 1';
    },
    scrollToVisual(id) {
      const idx = this.s.visuals.findIndex((v) => v.id === id);
      if (idx >= 0) this.padPage = idx;
    },
    sendDraft() {
      const text = this.draft.trim();
      if (!text || !sessionWs || sessionWs.readyState !== WebSocket.OPEN) return;
      pushBlock({ kind: 'user', text });
      sessionWs.send(JSON.stringify({ type: 'user', text }));
      this.draft = '';
    },
    answerQuestion(block, label = '') {
      const text = (block.responseText || '').trim();
      if (block.answered || (!label && !text)
        || !sessionWs || sessionWs.readyState !== WebSocket.OPEN) return;
      block.answered = label || text;
      sessionWs.send(JSON.stringify({
        type: 'answer_question', call_id: block.callId, label, text,
      }));
    },
    interrupt() {
      if (sessionWs && sessionWs.readyState === WebSocket.OPEN) {
        sessionWs.send(JSON.stringify({ type: 'interrupt' }));
      }
    },
    async fetchErd() {
      if (this.erdSchemas.length) return;
      try {
        const { data } = await axios.get('/api/impact_analytics/warehouse-catalog/');
        const schemas = (data.schemas || [])
          .map((s) => {
            const tables = {};
            for (const [name, t] of Object.entries(s.tables || {})) {
              if (t.use_for_metrics) tables[name] = t;
            }
            if (!Object.keys(tables).length) return null;
            return { name: s.name, tables, _open: false };
          })
          .filter(Boolean);
        this.erdSchemas = schemas;
      } catch {
        // catalog unavailable — panel stays empty
      }
    },
  },
};
</script>

<style scoped>
.ae-divider {
  width: 12px;
  margin: 0 -2px;
  flex-shrink: 0;
  cursor: col-resize;
  position: relative;
}
.ae-divider::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 5px;
  width: 2px;
  border-radius: 2px;
  background: #e5e4df;
  transition: background 0.15s;
}
.ae-divider:hover::after {
  background: #f59e0b;
}
</style>
